import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  type Identifier,
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
} from "ra-core";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { PickerButton } from "../activityLog/PickerButton";
import type { DealActivityType, DealStageActivity } from "../types";
import {
  computeStageActivityLinkDiff,
  toStageConfigErrorMessage,
} from "./stageConfigForm";

/**
 * Almikia Epic EA (TASK-115), closing EP's placeholder (STATUS.md EP entry,
 * QEP5): the catalogue half of "Actividades de la etapa" -- an ADDITIVE
 * multi-select picker over `deal_stage_activities` (DATA CONTRACT EA (3))
 * sitting next to the EP free-text `required_activities` list, which keeps
 * working unchanged. Local selection state only, committed by an explicit
 * "Guardar vínculos" so a batch of link/unlink writes either all lands or, on
 * a failure, is reported once with the user's picks kept on screen (ticket
 * acceptance criterion: no partial-save success). Mirrors `StageCriteriaSection`'s
 * shape (its own `useDataProvider` calls, its own Card) but needs a diff
 * across several rows instead of one immediate toggle, hence
 * `computeStageActivityLinkDiff`.
 */
export const StageActivitiesSection = ({
  stageKey,
  onDirtyChange,
}: {
  stageKey: string;
  /**
   * Reports whether this section holds unsaved link picks, so the sibling
   * main-form toolbar (`FormToolbar`'s `saveDisabled`) can block its own
   * submit instead of silently discarding them (ticket acceptance criterion).
   */
  onDirtyChange?: (dirty: boolean) => void;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const queryClient = useQueryClient();

  const [selectedIds, setSelectedIds] = useState<Set<Identifier> | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { data: activityTypes, isPending: activityTypesPending } =
    useGetList<DealActivityType>("deal_activity_types", {
      filter: { "archived_at@is": null },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "position", order: "ASC" },
    });
  const {
    data: links,
    isPending: linksPending,
    refetch,
  } = useGetList<DealStageActivity>("deal_stage_activities", {
    filter: { stage_key: stageKey },
    pagination: { page: 1, perPage: 200 },
  });

  const enabledIds = new Set(
    (links ?? [])
      .filter((link) => link.enabled)
      .map((link) => link.activity_type_id),
  );
  const current = selectedIds ?? enabledIds;
  const isDirty =
    selectedIds != null &&
    (selectedIds.size !== enabledIds.size ||
      [...selectedIds].some((id) => !enabledIds.has(id)));

  // Kept above the pending early return so hook order stays stable across
  // renders; reports up to `DealStageConfigEditForm` so the sibling main
  // "Guardar" can be blocked while this section holds unsaved picks.
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  if (activityTypesPending || linksPending || !activityTypes || !links) {
    return <Skeleton className="h-24 w-full" />;
  }

  const toggle = (activityTypeId: Identifier) => {
    const next = new Set(current);
    if (next.has(activityTypeId)) {
      next.delete(activityTypeId);
    } else {
      next.add(activityTypeId);
    }
    setSelectedIds(next);
  };

  const handleSave = async () => {
    if (!selectedIds) return;
    setIsSaving(true);
    try {
      const diff = computeStageActivityLinkDiff(selectedIds, links);
      const linkById = new Map(links.map((link) => [link.id, link]));

      await Promise.all([
        ...diff.toCreate.map((activityTypeId) =>
          dataProvider.create("deal_stage_activities", {
            data: {
              stage_key: stageKey,
              activity_type_id: activityTypeId,
              enabled: true,
            },
          }),
        ),
        ...diff.toEnable.map(({ id }) =>
          dataProvider.update("deal_stage_activities", {
            id,
            data: { enabled: true },
            previousData: linkById.get(id),
          }),
        ),
        ...diff.toDisable.map(({ id }) =>
          dataProvider.update("deal_stage_activities", {
            id,
            data: { enabled: false },
            previousData: linkById.get(id),
          }),
        ),
      ]);

      await refetch();
      queryClient.invalidateQueries({ queryKey: ["deal_stage_activities"] });
      setSelectedIds(null);
      notify("resources.deal_stage_configs.activities.saved", {
        type: "success",
      });
    } catch (error) {
      // The ticket's own acceptance criterion: never report success on a
      // partial save, and keep the user's picks on screen -- `selectedIds`
      // is untouched here, so nothing is lost. But `Promise.all` can partly
      // succeed (e.g. some creates land before one update fails), so `links`
      // must be refreshed here too: otherwise a retry diffs against stale
      // rows and re-creates a link that already exists, hitting the unique
      // (stage_key, activity_type_id) constraint (23505) on every retry.
      await refetch();
      queryClient.invalidateQueries({ queryKey: ["deal_stage_activities"] });
      notify(toStageConfigErrorMessage(error), { type: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-muted-foreground">
        {translate("resources.deal_stage_configs.activities.catalogue_title")}
      </h4>
      {activityTypes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.deal_stage_configs.activities.empty")}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {activityTypes.map((activity) => (
            <PickerButton
              key={activity.id}
              selected={current.has(activity.id)}
              onClick={() => toggle(activity.id)}
            >
              {activity.name}
            </PickerButton>
          ))}
        </div>
      )}
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="min-h-11"
        disabled={!isDirty || isSaving}
        onClick={handleSave}
      >
        {translate("resources.deal_stage_configs.activities.save_action")}
      </Button>
    </div>
  );
};
